import type { google } from '@alugha/ima';
import { loadImaSdk } from '@alugha/ima';
import type { MutableRefObject } from 'react';
import { useEffect, useRef, useState } from 'react';

/**
 * Called once the AdsManager is created and AD_ERROR is wired up, but before
 * `init()` / `start()` are called.  Use it to attach additional ad-event
 * listeners (e.g. STARTED, COMPLETE) that vary per use-case.
 */
type OnAdsManagerReadyFn = (manager: google.ima.AdsManager, ima: typeof google.ima, container: HTMLDivElement) => void;

type UseImaAdOptions = {
  onAdsManagerReady?: OnAdsManagerReadyFn;
  /**
   * Explicit [width, height] for IMA `AdsManager.init()`. Falls back to the
   * container's `offsetWidth/offsetHeight` when omitted (e.g. for thumbnails
   * whose size is determined by layout).
   */
  initDimensions?: readonly [number, number];
};

type UseImaAdReturn = {
  /** Attach this ref to the container `<div>` that IMA will render into. */
  adContainerRef: MutableRefObject<HTMLDivElement | null>;
  /** The live AdsManager instance — use for pause() / resume() etc. */
  adManagerRef: MutableRefObject<google.ima.AdsManager | null>;
  /** True while IMA SDK is loading and initialising (before start() resolves). */
  isLoading: boolean;
  /** True when IMA reports an unrecoverable error. */
  isError: boolean;
};

/**
 * Encapsulates the full IMA SDK lifecycle: load SDK → create
 * AdDisplayContainer → AdsLoader → requestAds → AdsManager → init/start →
 * cleanup.  Re-runs when `markupPayload` changes.
 */
export function useImaAd(markupPayload: string | null | undefined, options?: UseImaAdOptions): UseImaAdReturn {
  const adContainerRef = useRef<HTMLDivElement | null>(null);
  const imaRef = useRef<typeof google.ima | null>(null);
  const adContainerInstanceRef = useRef<google.ima.AdDisplayContainer | null>(null);
  const adLoaderRef = useRef<google.ima.AdsLoader | null>(null);
  const adManagerRef = useRef<google.ima.AdsManager | null>(null);
  const onAdsManagerReadyRef = useRef<OnAdsManagerReadyFn | undefined>(options?.onAdsManagerReady);
  const [isLoading, setIsLoading] = useState(() => !!markupPayload);
  const [isError, setIsError] = useState(false);

  useEffect(() => {
    if (!markupPayload || !adContainerRef.current) {
      setIsLoading(false);
      return;
    }

    setIsLoading(true);
    let cancelled = false;

    const init = async () => {
      try {
        imaRef.current = await loadImaSdk();
        if (cancelled || !adContainerRef.current) return;

        const ima = imaRef.current;

        adContainerInstanceRef.current = new ima.AdDisplayContainer(adContainerRef.current);
        adContainerInstanceRef.current.initialize();

        adLoaderRef.current = new ima.AdsLoader(adContainerInstanceRef.current);

        adLoaderRef.current.addEventListener(
          ima.AdsManagerLoadedEvent.Type.ADS_MANAGER_LOADED,
          (event: google.ima.AdsManagerLoadedEvent) => {
            // Capture before async guards so TypeScript narrows the type.
            const container = adContainerRef.current;
            if (cancelled || !container) return;

            adManagerRef.current = event.getAdsManager({ currentTime: 0, duration: 0 });

            adManagerRef.current.addEventListener(ima.AdErrorEvent.Type.AD_ERROR, () => {
              if (!cancelled) {
                setIsError(true);
                setIsLoading(false);
              }
            });

            onAdsManagerReadyRef.current?.(adManagerRef.current, ima, container);

            const width = options?.initDimensions?.[0] ?? (container.offsetWidth || 320);
            const height = options?.initDimensions?.[1] ?? (container.offsetHeight || 180);

            try {
              adManagerRef.current.init(width, height, ima.ViewMode.NORMAL);
              adManagerRef.current.start();
              if (!cancelled) {
                setIsError(false);
                setIsLoading(false);
              }
            } catch {
              if (!cancelled) {
                setIsError(true);
                setIsLoading(false);
              }
            }
          }
        );

        adLoaderRef.current.addEventListener(ima.AdErrorEvent.Type.AD_ERROR, () => {
          if (!cancelled) {
            setIsError(true);
            setIsLoading(false);
          }
        });

        const adsRequest = new ima.AdsRequest();
        adsRequest.adTagUrl = '';
        adsRequest.adsResponse = markupPayload;
        adLoaderRef.current.requestAds(adsRequest);
      } catch {
        if (!cancelled) {
          setIsError(true);
          setIsLoading(false);
        }
      }
    };

    void init();

    return () => {
      cancelled = true;
      adManagerRef.current?.destroy();
      adLoaderRef.current?.destroy();
      adContainerInstanceRef.current?.destroy();
      imaRef.current = null;
      adContainerInstanceRef.current = null;
      adLoaderRef.current = null;
      adManagerRef.current = null;
    };
  }, [markupPayload, options?.initDimensions?.[0], options?.initDimensions?.[1]]);

  return { adContainerRef, adManagerRef, isLoading, isError };
}
